import { useState } from "react";
import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import Star from "@mui/icons-material/Star";

import {
  BucketNumberEnum,
  useListBucketsQuery,
} from "features/trading/buckets";
import { MAIN_STAR_COLOR, sortMembers } from "./constants";
import { BucketEditModal } from "./BucketEditModal";
import { InlineLoading } from "./InlineLoading";

interface AccountBucketSummaryProps {
  accountId: number | null | undefined;
  accountLabel: string;
  number: BucketNumberEnum;
}

/**
 * Compact read-only view of an account's Bucket 1/2 for the report settings:
 * member chips (main assets starred), "Not created" when absent. Clicking
 * opens the shared BucketEditModal to add/remove assets.
 */
export const AccountBucketSummary = ({
  accountId,
  accountLabel,
  number,
}: AccountBucketSummaryProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { data: bucketsData, isFetching } = useListBucketsQuery(
    { account: accountId as number },
    { skip: !accountId },
  );
  const bucket = bucketsData?.results.find((b) => b.number === number);
  // On first load / account switch there is no data for the current account
  // yet — show a loading hint instead of a misleading "Not created".
  const isLoadingBuckets = isFetching && !bucketsData;

  if (!accountId) {
    return (
      <Typography variant="body2" color="text.disabled">
        Select an account
      </Typography>
    );
  }

  if (isLoadingBuckets) {
    return <InlineLoading />;
  }

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        flexWrap="wrap"
        gap={0.5}
        sx={{ cursor: "pointer" }}
        onClick={() => setIsModalOpen(true)}
      >
        {!bucket ? (
          <Chip size="small" variant="outlined" label="Not created" />
        ) : bucket.assets.length === 0 ? (
          <Chip size="small" variant="outlined" label="Empty" />
        ) : (
          sortMembers(bucket.assets).map((member) => (
            <Chip
              key={member.id}
              size="small"
              label={member.symbol}
              icon={
                member.is_main ? (
                  <Star fontSize="small" htmlColor={MAIN_STAR_COLOR} />
                ) : undefined
              }
            />
          ))
        )}
        <Tooltip title={`Edit Bucket ${number}`}>
          <IconButton size="small">
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <BucketEditModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        accountId={accountId}
        accountLabel={accountLabel}
        number={number}
        bucket={bucket}
      />
    </>
  );
};
